import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/components/customAxios/Axios";
import { useLoader } from "@/context/loaderContext";
import logo from "@/assets/talentick.ai.webp";
import { AiOutlineEye, AiOutlineEyeInvisible } from "react-icons/ai";
import { useToast } from "@/components/toast";
import Tooltip from "@/components/Tooltip";
const RegisterAdmin: React.FC = () => {
	const { setLoading } = useLoader();
	const toast = useToast();
	const navigate = useNavigate();
	const [email, setEmail] = useState("");
	const [password, setPassword] = useState("");
	const [firstName, setFirstName] = useState("");
	const [lastName, setLastName] = useState("");
	const [confirmPassword, setConfirmPassword] = useState("");
	const [showPassword, setShowPassword] = useState(false);
	const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		if (password !== confirmPassword) {
			toast.error({
				title: "Error",
				description: "Passwords do not match.",
				duration: 5000,
				open: true,
				status: "error",
			});
			return;
		}
		if (!email.endsWith("bluetickconsultants.com")) {
			toast.error({
				title: "Error",
				description: "Invalid email domain.",
				duration: 5000,
				open: true,
				status: "error",
			});
			return;
		}
		try {
			setLoading(true);
			const res = await api.post("/register_admin", {
				first_name: firstName,
				last_name: lastName,
				email,
				password,
			});

			if (res.status === 200) {
				setLoading(false);
				navigate("/login");
				toast.success({
					title: "Verify Email",
					description: "An email has been sent for verification.",
					duration: 5000,
					open: true,
					status: "success",
				});
			} else if (res.status === 401) {
				setLoading(false);
				toast.error({
					title: "Error",
					description: "Email is already registered",
					duration: 5000,
					open: true,
					status: "error",
				});
			}
		} catch (err: any) {
			setLoading(false);
			toast.error({
				title: "Error",
				description: err.response?.data?.error || err.response?.data?.message || "An error occurred.",
				duration: 5000,
				open: true,
				status: "error",
			});
		}
	};

	return (
		<div>
			<div className="flex justify-between items-center mx-2 md:mx-16 lg:mx-32 border-b-2">
				<Link to="/">
					<img src={logo} className="w-40 md:w-64" />
				</Link>

				<Link to="/login">
					<button className="mr-5 px-4 py-1   rounded-xl font-medium tracking-wide text-white capitalize transition-colors duration-300 transform  bg-gradient-to-r from-[#3895D3] to-[#9039D2]">
						Log In
					</button>
				</Link>
			</div>

			<div
				className="bg-gray-50 flex items-center justify-center py-2"
				id="main-content"
			>
				<div className="flex justify-center items-center  bg-gray-100 dark:bg-zinc-900 p-4">
					<div className="bg-white dark:bg-zinc-800 shadow-lg rounded-lg p-6 md:p-10 w-full max-w-xl">
						<h2 className="md:text-3xl text-xl  font-heading font-bold text-center text-zinc-800  mb-3">
							Create an Account
						</h2>

						<form className="space-y-5" method="POST" onSubmit={handleSubmit}>
							<div className="grid md:grid-cols-2 gap-4">
								<div>
									<label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300">
										First Name <span className="text-red-500">*</span>
									</label>
									<input
										onChange={(e) => setFirstName(e.target.value)}
										name="first_name"
										type="text"
										required
										placeholder="First Name"
										className="w-full border rounded-lg py-2 px-3 text-zinc-700 dark:text-zinc-300 bg-transparent "
									/>
								</div>
								<div>
									<label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300">
										Last Name <span className="text-red-500">*</span>
									</label>
									<input
										onChange={(e) => setLastName(e.target.value)}
										name="last_name"
										type="text"
										required
										placeholder="Last Name"
										className="w-full border rounded-lg py-2 px-3 text-zinc-700 dark:text-zinc-300 bg-transparent "
									/>
								</div>
							</div>

							<div>
								<label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300">
									Email <span className="text-red-500">*</span>
								</label>
								<input
									onChange={(e) => setEmail(e.target.value)}
									name="email"
									type="email"
									autoComplete="email"
									required
									placeholder="Email"
									className="w-full border rounded-lg py-2 px-3 text-zinc-700 dark:text-zinc-300 bg-transparent "
								/>
							</div>

							<div className="relative">
								<label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300">
									Password{" "}
									<Tooltip>
										<ul className=" list-disc pl-4">
											<li>At least 6 characters long</li>
											<li>At least one uppercase letter</li>
											<li>At least one number</li>
											<li>At least one special character (@$!%*?&)</li>
										</ul>
									</Tooltip>
								</label>
								<div className="relative">
									<input
										type={showPassword ? "text" : "password"}
										id="password"
										autoComplete="password"
										value={password}
										pattern="(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{6,}"
										onChange={(e) => setPassword(e.target.value)}
										className="w-full border rounded-lg py-2 px-3 text-zinc-700 dark:text-zinc-300 bg-transparent  pr-10"
										placeholder="Password"
										required
									/>
									<button
										type="button"
										onClick={() => setShowPassword(!showPassword)}
										className="absolute inset-y-0 right-3 flex items-center text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-400"
									>
										{showPassword ? (
											<AiOutlineEyeInvisible size={20} />
										) : (
											<AiOutlineEye size={20} />
										)}
									</button>
								</div>
							</div>

							<div>
								<label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300">
									Confirm Password <span className="text-red-500">*</span>
								</label>
								<input
									onChange={(e) => setConfirmPassword(e.target.value)}
									name="confirm_password"
									type="password"
									autoComplete="new-password"
									required
									placeholder="Confirm Password"
									className="w-full border rounded-lg py-2 px-3 text-zinc-700 dark:text-zinc-300 bg-transparent "
								/>
							</div>

							<button
								type="submit"
								className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-lg  transition"
							>
								Register
							</button>

							<p className="text-center text-sm text-zinc-600 dark:text-zinc-300 mt-4">
								Already have an account?{" "}
								<Link
									to="/login"
									className="text-blue-500 hover:text-blue-800 dark:hover:text-blue-300"
								>
									Login
								</Link>
							</p>
						</form>
					</div>
				</div>
			</div>
		</div>
	);
};

export default RegisterAdmin;
